/**
 * A console logger whose methods are replaced by no-ops below the configured
 * level, so a suppressed call costs nothing and keeps the caller's file and
 * line in the browser console — which a wrapper function would not.
 *
 * # Why the constructor takes a name
 *
 * It originally took only a numeric level, and seven of the eight call sites
 * in `ainova-app` passed a string instead — `new Log('Sentry')`,
 * `new Log('Turnstile')`, `new Log('ErrorHandler')` and so on. A string level
 * makes every `level >= Log.ERROR` comparison false, because the string
 * converts to NaN, so all six methods were replaced with no-ops and those
 * modules logged nothing at all. Sentry reporting, the Meta pixel, the error
 * handler and Turnstile were all silent in production.
 *
 * The call sites were not wrong about what they wanted; the constructor was
 * wrong about what it accepted. A name is now a name, and it is bound as a
 * leading console argument rather than wrapped in a formatting function, so
 * the browser still attributes each line to the file that logged it.
 */
export class Log {
  static readonly ASSERT = 1;
  static readonly ERROR = 2;
  static readonly WARN = 3;
  static readonly INFO = 4;
  static readonly DEBUG = 5;
  static readonly TRACE = 6;

  assert = console.assert.bind(console);
  error = console.error.bind(console);
  warn = console.warn.bind(console);
  info = console.info.bind(console);
  debug = console.debug.bind(console);
  trace = console.log.bind(console);

  level = Log.INFO;

  /** A prefix for this logger's lines, e.g. `[Turnstile]`. Empty if unnamed. */
  readonly name: string;

  /**
   * @param nameOrLevel a logger name, or the level when given as a number
   * @param level the level, when a name was given
   */
  constructor(nameOrLevel?: string | number, level?: number) {
    this.name = typeof nameOrLevel === 'string' ? nameOrLevel : '';

    const resolved = typeof nameOrLevel === 'number' ? nameOrLevel : level;
    if (resolved !== undefined) {
      this.level = resolved;
    }
    this.setLevel(this.level);
  }

  setLevel(level: number) {
    this.level = level;

    // Binding the prefix as a leading argument keeps `console.error` itself as
    // the callee, so devtools still points at the caller rather than at this
    // file. A wrapper function would report every line as coming from here.
    const prefix: [string?] = this.name ? [`[${this.name}]`] : [];
    const noop = () => {};
    const bind = (method: (...args: unknown[]) => void, enabled: boolean) =>
      enabled ? method.bind(console, ...prefix) : noop;

    // `console.assert` takes the condition first, so the prefix cannot be
    // bound onto it: a leading string would become the condition and every
    // assertion would pass.
    this.assert = enabledAt(level, Log.ASSERT) ? console.assert.bind(console) : noop;
    this.error = bind(console.error, enabledAt(level, Log.ERROR));
    this.warn = bind(console.warn, enabledAt(level, Log.WARN));
    this.info = bind(console.info, enabledAt(level, Log.INFO));
    this.debug = bind(console.debug, enabledAt(level, Log.DEBUG));
    this.trace = bind(console.log, enabledAt(level, Log.TRACE));
  }
}

/** Is `threshold` at or below the configured level? */
function enabledAt(level: number, threshold: number): boolean {
  return level >= threshold;
}
